import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { PrivateRoute } from './private-route'
import { PermissionRoute } from './permission-route'
import { BillingConfigRoute } from './billing-config-route'
import { AppLayout } from './layouts/app-layout'
import { LoadingSpinner } from '@/components/ui/loading-spinner'

// Lazy loading das páginas - cada página será um chunk separado
const PainelPage = lazy(() => import('@/pages/painel'))
const LoginPage = lazy(() => import('@/pages/login'))
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password'))
const ResetPasswordPage = lazy(() => import('@/pages/reset-password'))
const RegisterPage = lazy(() => import('@/pages/register'))
const TermsOfUsePage = lazy(() =>
  import('@/pages/legal/terms-of-use').then((m) => ({ default: m.TermsOfUsePage })),
)
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/legal/privacy-policy').then((m) => ({ default: m.PrivacyPolicyPage })),
)
const ForbiddenPage = lazy(() => import('@/pages/forbidden'))
const NotFoundPage = lazy(() => import('@/pages/not-found'))

const StudentsPage = lazy(() => import('@/pages/students'))
const UsersPage = lazy(() => import('@/pages/users'))
const ConfiguracoesPage = lazy(() => import('@/pages/configuracoes'))
const ModalitiesPage = lazy(() => import('@/pages/modalities'))
const CalendarioPage = lazy(() => import('@/pages/calendario'))
const PlansPage = lazy(() => import('@/pages/plans'))
const PlanCreatePage = lazy(() => import('@/pages/plans/create'))
const PlanEditPage = lazy(() => import('@/pages/plans/edit'))
const CheckinPage = lazy(() => import('@/pages/checkin'))
const CheckinAnalyticsPage = lazy(() => import('@/pages/checkin-analytics'))
const SubscriptionPage = lazy(() => import('@/pages/subscription'))
const ContasReceberPage = lazy(() => import('@/pages/financeiro/contas-a-receber'))
const InadimplenciaPage = lazy(() => import('@/pages/financeiro/inadimplencia'))
const ResumoFinanceiroPage = lazy(() => import('@/pages/financeiro/resumo'))
const GraduacoesPage = lazy(() => import('@/pages/graduacoes'))
const RelatoriosFinanceiroPage = lazy(() =>
  import('@/pages/financeiro/relatorios').then((m) => ({ default: m.RelatoriosFinanceiroPage })),
)

function PageLoader() {
  return (
    <div className='flex h-[50vh] items-center justify-center'>
      <LoadingSpinner size='lg' />
    </div>
  )
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path='/login' element={<LoginPage />} />
        <Route path='/forgot-password' element={<ForgotPasswordPage />} />
        <Route path='/reset-password' element={<ResetPasswordPage />} />
        <Route path='/403' element={<ForbiddenPage />} />
        <Route path='/404' element={<NotFoundPage />} />
        <Route path='/register' element={<RegisterPage />} />
        <Route path='/terms-of-use' element={<TermsOfUsePage />} />
        <Route path='/privacy-policy' element={<PrivacyPolicyPage />} />

        <Route element={<PrivateRoute />}>
          <Route element={<AppLayout />}>
              <Route element={<PermissionRoute permission='dashboard:read' />}>
                <Route path='/' element={<PainelPage />} />
              </Route>

              <Route element={<PermissionRoute permission='students:read' />}>
                <Route path='/alunos' element={<StudentsPage />} />
              </Route>

              <Route element={<PermissionRoute permission='modalities:read' />}>
                <Route path='/modalidades' element={<ModalitiesPage />} />
              </Route>

              <Route element={<PermissionRoute permission='calendar:read' />}>
                <Route path='/calendario' element={<CalendarioPage />} />
              </Route>

              <Route element={<PermissionRoute permission='graduation:read' />}>
                <Route path='/graduacoes' element={<GraduacoesPage />} />
              </Route>

              <Route element={<PermissionRoute permission='plans:read' />}>
                <Route path='/planos' element={<PlansPage />} />
                <Route path='/planos/novo' element={<PlanCreatePage />} />
                <Route path='/planos/:id/editar' element={<PlanEditPage />} />
              </Route>

              <Route element={<PermissionRoute permission='financial:read' />}>
                <Route element={<BillingConfigRoute />}>
                  <Route path='/financeiro/contas-a-receber' element={<ContasReceberPage />} />
                  <Route path='/financeiro/inadimplencia' element={<InadimplenciaPage />} />
                  <Route path='/financeiro/resumo' element={<ResumoFinanceiroPage />} />
                  <Route path='/financeiro/relatorios' element={<RelatoriosFinanceiroPage />} />
                </Route>
              </Route>

              <Route element={<PermissionRoute permission='checkin:read' />}>
                <Route path='/checkin' element={<CheckinAnalyticsPage />} />
                <Route path='/configuracoes/checkin' element={<CheckinPage />} />
              </Route>

              <Route element={<PermissionRoute permission='users:read' />}>
                <Route path='/usuarios' element={<UsersPage />} />
              </Route>

              <Route element={<PermissionRoute permission='subscription:read' />}>
                <Route path='/assinatura' element={<SubscriptionPage />} />
              </Route>
              <Route element={<PermissionRoute permission='configuracoes:read' />}>
                <Route path='/configuracoes' element={<ConfiguracoesPage />} />
              </Route>
            </Route>
        </Route>

        <Route path='*' element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}
